import { hoverStyle } from "@/styles/theme";
import { ExternalIcon } from "@atoms/icon/ExternalIcon";
import { ListItemIcon } from "@atoms/icon/ListItemIcon";
import type { SxProps, Theme } from "@mui/material";
import {
  ListItemButton,
  ListItemText,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Link from "next/link";
import type { ComponentProps } from "react";

type IconBaseProps = {
  iconPath: string;
  width?: number;
  height?: number;
  alt?: string;
  showFull?: boolean;
};

export type ListItemProps = IconBaseProps & {
  text: string;
  href: string;
  isSelected?: boolean;
  external?: Partial<IconBaseProps> | null;
  onClick?: ComponentProps<typeof Link>["onClick"];
  sx?: SxProps<Theme>; // sxプロパティを追加
};

export const ListItem = ({
  text,
  iconPath,
  width,
  height,
  alt,
  href,
  external = null,
  isSelected = false,
  showFull = true,
  onClick,
}: ListItemProps) => {
  const externalProps = external
    ? {
        target: "_blank",
        rel: "noopener noreferrer",
        ...external,
      }
    : null;

  const content = (
    <ListItemButton
      component={Link}
      href={href}
      target={externalProps?.target}
      rel={externalProps?.rel}
      onClick={externalProps ? undefined : onClick}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        textDecoration: "none",
        color: "inherit",
        width: "fit-content",
        padding: 0,
        cursor: isSelected ? "auto" : "pointer",
        borderRadius: 2,
        ...(!isSelected && hoverStyle),
      }}
    >
      <ListItemIcon
        iconPath={iconPath}
        alt={alt || text}
        width={width || 40}
        height={height || 40}
      />
      {showFull && (
        <>
          <ListItemText
            sx={{
              cursor: isSelected ? "default" : "inherit",
            }}
          >
            {text}
          </ListItemText>
          {externalProps && (
            <MuiListItemIcon>
              <ExternalIcon
                width={externalProps.width || 16}
                height={externalProps.height || 16}
              />
            </MuiListItemIcon>
          )}
        </>
      )}
    </ListItemButton>
  );

  return (
    <MuiListItem
      sx={{
        width: `${showFull ? "184px" : width ? `${width}px` : "40px"}`,
        padding: `${showFull ? 1 : 2}`,
        backgroundColor: isSelected
          ? (theme) => theme.palette.primary.main
          : "inherit",
        color: isSelected ? (theme) => theme.palette.common.white : "inherit",
        borderRadius: 2,
        mb: 1.5,
      }}
      disablePadding
    >
      {showFull ? (
        content
      ) : (
        <Tooltip title={text} placement="right">
          {content}
        </Tooltip>
      )}
    </MuiListItem>
  );
};
